import { useCallback, useEffect, useRef, useState } from 'react';

import { LEADS_REGISTRY_ROW_HEIGHT } from '@app/pages/Leads/LeadsRegistryPage/model/constants';
import type { LeadsRegistryTableRow } from '@app/pages/Leads/LeadsRegistryPage/model/types';

import type { RefObject } from 'react';

// Место в списке привязано к вкладке: во второй вкладке у пользователя своя
// позиция, а на следующий день старая уже не нужна — отсюда sessionStorage.
const STORAGE_KEY = 'leads-registry:scroll-anchor';

// Подсветка строки, из которой вернулись: глазу нужно за что-то зацепиться,
// иначе непонятно, то же это место или список просто открылся где-то.
const HIGHLIGHT_DURATION_MS = 2000;

interface LeadsRegistryScrollAnchor {
  leadId: number;
  // Смещение строки от верха видимой области в момент клика: с ним лид
  // возвращается ровно туда же по вертикали, а не просто «куда-то в список».
  offsetTop: number;
  // Подпись фильтров: при других фильтрах список другой, и восстанавливать
  // место бессмысленно.
  signature: string;
}

const isAnchor = (value: unknown): value is LeadsRegistryScrollAnchor => {
  if (typeof value !== 'object' || value === null) return false;

  const candidate = value as Record<string, unknown>;

  return (
    typeof candidate.leadId === 'number' &&
    typeof candidate.offsetTop === 'number' &&
    typeof candidate.signature === 'string'
  );
};

const readAnchor = (): LeadsRegistryScrollAnchor | null => {
  try {
    const rawValue = window.sessionStorage.getItem(STORAGE_KEY);
    if (!rawValue) return null;

    const parsed: unknown = JSON.parse(rawValue);

    return isAnchor(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const writeAnchor = (anchor: LeadsRegistryScrollAnchor): void => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(anchor));
  } catch {
    // Приватный режим или заблокированное хранилище: список просто откроется
    // сверху, как и раньше.
  }
};

const clearAnchor = (): void => {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // См. writeAnchor.
  }
};

interface UseLeadsRegistryScrollRestoreParams {
  rows: LeadsRegistryTableRow[];
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  searchSignature: string;
}

interface UseLeadsRegistryScrollRestoreResult {
  // Вызывается при открытии карточки лида: запоминает, где лид стоял.
  rememberLeadPosition(leadId: number): void;
  // Лид, к которому вернулись: строка подсвечивается на пару секунд.
  highlightedLeadId: number | null;
}

const findLeadIndex = (rows: LeadsRegistryTableRow[], leadId: number): number =>
  rows.findIndex((row) => row.kind === 'lead' && row.id === leadId);

export const useLeadsRegistryScrollRestore = ({
  rows,
  scrollContainerRef,
  searchSignature,
}: UseLeadsRegistryScrollRestoreParams): UseLeadsRegistryScrollRestoreResult => {
  const [highlightedLeadId, setHighlightedLeadId] = useState<number | null>(
    null
  );
  const isRestoredRef = useRef(false);

  const rememberLeadPosition = useCallback(
    (leadId: number) => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const index = findLeadIndex(rows, leadId);
      if (index < 0) return;

      writeAnchor({
        leadId,
        offsetTop: index * LEADS_REGISTRY_ROW_HEIGHT - container.scrollTop,
        signature: searchSignature,
      });
    },
    [rows, scrollContainerRef, searchSignature]
  );

  useEffect(() => {
    if (isRestoredRef.current) return;

    const container = scrollContainerRef.current;
    if (!container || !rows.length) return;

    const anchor = readAnchor();
    if (!anchor) {
      isRestoredRef.current = true;

      return;
    }

    // Фильтры сменились — список другой, старое место к нему не относится.
    if (anchor.signature !== searchSignature) {
      isRestoredRef.current = true;
      clearAnchor();

      return;
    }

    const index = findLeadIndex(rows, anchor.leadId);

    // Лида в выборке больше нет: ставим сохранённый скролл как есть и никого
    // не подсвечиваем — подсвечивать нечего.
    const nextScrollTop =
      index < 0
        ? Math.max(0, -anchor.offsetTop)
        : Math.max(0, index * LEADS_REGISTRY_ROW_HEIGHT - anchor.offsetTop);

    isRestoredRef.current = true;
    clearAnchor();
    container.scrollTop = nextScrollTop;

    if (index >= 0) setHighlightedLeadId(anchor.leadId);
  }, [rows, scrollContainerRef, searchSignature]);

  useEffect(() => {
    if (highlightedLeadId === null) return undefined;

    const timeoutId = window.setTimeout(
      () => setHighlightedLeadId(null),
      HIGHLIGHT_DURATION_MS
    );

    return () => window.clearTimeout(timeoutId);
  }, [highlightedLeadId]);

  return { rememberLeadPosition, highlightedLeadId };
};
